import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { RadioGroup as ShadcnRadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { accountSettingsNestedCardClassName } from "../accountSettingsCardShell";
import NotificationEventAccountPicker from "./NotificationEventAccountPicker";

export interface NotificationEventPreferenceRowProps {
  eventKey: string;
  title: string;
  inApp: boolean;
  email: boolean;
  showEmailToggle: boolean;
  mode: "all" | "selected";
  selectedCodes: string[];
  hasScopeError: boolean;
  accountsLoading: boolean;
  accountCodeOptions: string[];
  onToggleInApp: () => void;
  onToggleEmail: () => void;
  onScopeModeChange: (mode: "all" | "selected") => void;
  onScopeAccountsChange: (codes: string[]) => void;
}

export function NotificationEventPreferenceRow({
  eventKey,
  title,
  inApp,
  email,
  showEmailToggle,
  mode,
  selectedCodes,
  hasScopeError,
  accountsLoading,
  accountCodeOptions,
  onToggleInApp,
  onToggleEmail,
  onScopeModeChange,
  onScopeAccountsChange,
}: NotificationEventPreferenceRowProps): React.ReactElement {
  const allScopeId = `notification-scope-all-${eventKey}`;
  const selectedScopeId = `notification-scope-selected-${eventKey}`;

  return (
    <div
      data-notification-event-row=""
      className={cn(accountSettingsNestedCardClassName, "h-full")}
    >
      <div className="flex flex-col gap-3 px-[22px] py-[22px]">
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{title}</FieldLabel>
        </Field>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span className={`${BROKER_FIELD_DESC_CLASS} leading-none`}>In-app</span>
            <Switch checked={inApp} onCheckedChange={onToggleInApp} />
          </div>
          <div className="flex items-center gap-2">
            <span className={`${BROKER_FIELD_DESC_CLASS} leading-none`}>Email</span>
            {showEmailToggle ? (
              <Switch checked={email} onCheckedChange={onToggleEmail} />
            ) : (
              <span className={BROKER_FIELD_DESC_CLASS}>—</span>
            )}
          </div>
        </div>

        <Field className="gap-2" data-invalid={hasScopeError || undefined}>
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Account scope</FieldLabel>
          <ShadcnRadioGroup
            value={mode}
            onValueChange={(value) => onScopeModeChange(value as "all" | "selected")}
            className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2"
            data-account-settings-radio-group=""
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value="all"
                id={allScopeId}
                data-account-settings-radio=""
              />
              <FieldLabel
                htmlFor={allScopeId}
                className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal leading-none`}
              >
                All accounts
              </FieldLabel>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value="selected"
                id={selectedScopeId}
                data-account-settings-radio=""
              />
              <FieldLabel
                htmlFor={selectedScopeId}
                className={`${BROKER_FIELD_DESC_CLASS} cursor-pointer font-normal leading-none`}
              >
                Selected accounts
              </FieldLabel>
            </div>
          </ShadcnRadioGroup>

          {mode === "selected" ? (
            <div className="mt-1 w-fit max-w-full">
              {accountsLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="size-3.5 animate-spin text-muted-foreground" aria-hidden />
                  <span className={BROKER_FIELD_DESC_CLASS}>Loading accounts...</span>
                </div>
              ) : accountCodeOptions.length === 0 ? (
                <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                  No broker accounts available yet. Connect or sync an account first.
                </FieldDescription>
              ) : (
                <>
                  <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                    Choose which accounts should receive this notification.
                  </FieldDescription>
                  <NotificationEventAccountPicker
                    accountCodeOptions={accountCodeOptions}
                    selectedCodes={selectedCodes}
                    onChange={onScopeAccountsChange}
                  />
                </>
              )}
            </div>
          ) : null}

          {hasScopeError ? (
            <FieldDescription className="text-xs text-destructive">
              Select at least one account for selected scope.
            </FieldDescription>
          ) : null}
        </Field>
      </div>
    </div>
  );
}
